// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
/* CheckMark.js
Include to draw & activate a checkmark or X-mark
to indicate correct & incorrect answers
*/

import utils from '../utils';

const icons = {
  check:
    '<svg width="100%" height="100%" viewBox="0 175 214 225" ><g stroke="#2EC592" fill="#2EC592">   <path d="M67.4,347.3c8.6-19.7,17.9-38.5,27.8-56.5c9.9-18,20.1-34.5,30.6-49.7c12.4-17.8,22.1-30.7,29.2-38.5      c4.6-5.1,8.9-9.2,12.9-12.5c2.3-1.9,6-3.3,11.1-4.2c12.3-2.2,20.9-3.4,25.7-3.4c2.2,0,3.2,0.8,3.2,2.2c0,1.4-1.1,3.1-3.2,5.1 c-18.8,16.7-38.4,40.5-59,71.6s-37.9,64-52.1,98.9c-5.3,13-9.1,21-11.2,24c-2.1,3-8.7,4.4-19.8,4.4c-7.2,0-11.7-0.7-13.6-2.1 c-1.8-1.4-5.1-5.9-9.9-13.4c-8.3-12.5-17.1-23.7-26.6-33.8c-5-5.2-7.5-9.3-7.5-12.1c0-3.9,2.8-7.8,8.3-11.6 c5.5-3.8,10.1-5.7,13.6-5.7c5,0,10.9,2.8,17.9,8.4C52.1,324,59.5,333.7,67.4,347.3z"/> </g></svg>',
  x: '<svg width="100%" height="100%" viewBox="0 184 193 216" stroke="red" fill="red"> <g><path d="M70.3,288.6L52.5,216c-0.8-2.3-1.1-4.5-1.1-6.3c0-5.6,1.6-10.4,4.8-14.3c3.2-3.9,6.7-5.9,10.7-5.9      c7.1,0,14.6,4.9,22.4,14.8c7.8,9.9,14.9,23.1,21.4,39.6c1.8-1.4,9.6-9.7,23.3-24.8c11.3-12.4,18.8-19.9,22.3-22.5     c3.5-2.6,7.2-3.9,11.2-3.9c4.9,0,9.4,2.4,13.6,7.1c4.2,4.7,6.3,9.6,6.3,14.7c0,4.6-2.5,7-7.5,7.3c-1.7,0-2.8,0.2-3.2,0.6l-0.7,2.7     c-0.3,0.8-1.3,2.3-3,4.5c-5.2,5.8-12.9,16.7-23.1,32.8c-11.3,17.5-17.8,27.1-19.3,28.8c-2.3,2.9-3.5,5.9-3.5,9      c0,6.5,2.1,15.3,6.3,26.4c4.2,11.2,9.9,21.8,17.1,31.9c3.1,4.7,4.6,7.8,4.6,9.3c0,0.9-0.8,2.7-2.4,5.2c0.7,0.8,1,1.7,1,2.5      c0,1.8-0.6,3.1-1.7,3.9c-1.1,0.8-3.8,2.3-7.9,4.5c0,5.5-2.3,8.3-6.9,8.3c-2,0-4.7-1.2-8.3-3.5c-3.6-2.3-6-4.5-7.3-6.6     c-1.4-1.7-2.8-2.8-4.2-3.4c-1.5-0.4-2.6-0.9-3.4-1.5c-0.8-0.6-2.5-2.4-5.3-5.4c-0.2-0.4-1.3-1.2-3.4-2.4c-2.6-1.1-5.2-4.1-7.6-8.9     c-1.2-3-4.5-8.6-9.7-16.7c-30.7,33.1-46.8,50.6-48.5,52.4c-1.7,1.8-3.7,3.8-6,5.8c-2.3,2.1-4.2,3.1-5.6,3.1     c-1.6,0-2.9-0.5-3.9-1.4c-1-0.9-1.5-4-1.6-9.3l-1.8-2c-0.6-0.6-1.1-2-1.7-4.4l-2.8-1.4c-1.5-2.8-4-5.8-7.6-8.9L8,373.5l-0.8-3.8     c0-1.1,0.6-3,1.8-5.5c1.2-2.5,7-10.1,17.5-22.9s17-20.2,19.4-22.6c9.2-8.6,14.1-14.3,14.6-17C62.4,294.4,65.7,290,70.3,288.6z"/></g></svg>',
};

interface CheckMarkOptions {
  /** in seconds */
  rightAnswerLength: number;
  /** in seconds */
  wrongAnswerLength: number;
  /** in seconds */
  warningLength: number;
  /** in seconds */
  textLength: number;
  /** css easing value */
  ease: string;
}

export default class {
  private timeouts: number[] = [];

  options: CheckMarkOptions;

  private displayDiv: HTMLDivElement;

  private checkDiv: HTMLDivElement;

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: Partial<CheckMarkOptions> = {}) {
    this.timeouts = [];

    this.options = utils.merge(
      {
        rightAnswerLength: 0.5,
        wrongAnswerLength: 1.0,
        warningLength: 4.0,
        textLength: 4,
        ease: 'ease-in-out',
      },
      options
    );

    this.displayDiv = displayDiv;
    this.initializeDiv();
    this.draw('off');
    displayDiv.style['will-change'] = 'opacity';
  }

  private initializeDiv(): void {
    const checkDiv = document.createElement('div');

    checkDiv.style.opacity = '0';
    checkDiv.style.position = 'absolute';
    checkDiv.style.zIndex = '24';
    checkDiv.style.top = '0';
    checkDiv.style.left = '0';
    checkDiv.style.width = '100%';
    checkDiv.style.height = '100%';

    checkDiv.style.pointerEvents = 'none';

    this.displayDiv.appendChild(checkDiv);
    this.checkDiv = checkDiv;
    this.checkDiv.setAttribute('aria-live', 'polite');
  }

  private setDuration(duration): this {
    const easing = this.options.ease;
    const transitionString = `opacity ${duration}s ${easing}`;
    this.checkDiv.style['-webkit-transition'] = transitionString;
    this.checkDiv.style['-moz-transition'] = transitionString;
    this.checkDiv.style['-ms-transition'] = transitionString;
    this.checkDiv.style['-o-transition'] = transitionString;
    this.checkDiv.style.transition = transitionString;
    return this;
  }

  private reveal(): this {
    this.checkDiv.style.opacity = '1';
    return this;
  }

  // private hide() {
  //   this.checkDiv.style.opacity = '0';
  //   return this;
  // }

  resize(): void {
    this.checkDiv.style.height = '100%';
    this.checkDiv.style.width = '100%';
  }

  private draw(shapeToDraw: string, alternateAriaString?: string): this {
    let outputHTML = null;
    let ariaLabel = '';

    switch (shapeToDraw) {
      case 'check':
        ariaLabel = alternateAriaString || 'correct';
        outputHTML = icons.check;
        break;
      case 'x':
        ariaLabel = alternateAriaString || 'wrong';
        outputHTML = icons.x;
        break;
      default:
        outputHTML = '';
        break;
    }

    this.checkDiv.setAttribute('aria-label', ariaLabel);
    this.checkDiv.setAttribute('role', 'alert');
    this.checkDiv.innerHTML = outputHTML;
    this.checkDiv.style.backgroundColor = 'transparent';
    return this;
  }

  /**
   * Display a warning text
   * @param text
   * @param color default red
   */
  text(text: string, color = '#FF4444'): this {
    let outputHTML =
      `<span style="color:${color};font-size:8vw;` +
      'text-weight:bold;text-shadow: 0.5vw      0.25vw      1vw        #222222;">';
    this.checkDiv.style.backgroundColor = 'rgba(200,200,200,0.5)';

    outputHTML += text;
    outputHTML += '</span>';
    this.checkDiv.setAttribute('aria-label', text);
    this.checkDiv.setAttribute('role', 'alert');
    this.checkDiv.setAttribute('aria-live', 'assertive')
    this.checkDiv.innerHTML = outputHTML;
    this.checkDiv.setAttribute('aria-live', 'polite');
    this.checkDiv.removeAttribute('aria-label');
    return this;
  }

  /**
   * Hide whatever's showing, immediately!
   */
  off(): this {
    this.checkDiv.innerHTML = '';
    this.checkDiv.removeAttribute('role');
    this.checkDiv.setAttribute('aria-label', '');
    this.go(0);
    return this;
  }

  private clearTimeouts(): this {
    for (let i = this.timeouts.length - 1; i >= 0; i--) {
      window.clearTimeout(this.timeouts[i]);
      this.timeouts.length = i;
    }
    return this;
  }

  private go(duration: number, delay = 0): this {
    // duration = transition speed in and out
    // delay = how long to remain at full opacity

    this.clearTimeouts();

    const div = this.checkDiv;
    this.setDuration(duration / 2);

    // eslint-disable-next-line no-param-reassign
    delay += duration / 2;
    // eslint-disable-next-line no-param-reassign
    delay *= 1000; // convert to MS

    this.reveal();

    const timeoutID = window.setTimeout(() => {
      div.removeAttribute('role');
      div.removeAttribute('aria-label');
      div.style.opacity = '0';
      this.checkDiv.removeAttribute('aria-label');
      this.checkDiv.removeAttribute('role');
    }, delay);

    this.timeouts.push(timeoutID);
    return this;
  }

  /**
   * Show a right answer √
   * @param duration in seconds
   */
  check(duration = this.options.rightAnswerLength): Promise<this> {
    this.draw('check');
    this.go(duration);
    return utils.wait(duration).then(() => this);
  }

  /**
   * Show a wrong answer X
   * @param duration in seconds
   */
  x(duration = this.options.wrongAnswerLength, alternateAriaText?: string): Promise<this> {
    if ('vibrate' in window.navigator) {
      window.navigator.vibrate(150);
    }
    this.draw('x', alternateAriaText);
    this.go(duration);
    return utils.wait(duration).then(() => this);
  }

  /**
   * Show a wrong answer X then display text
   * @param text to display following X
   * @param duration in seconds
   */
  xAndWarn(text: string, duration = this.options.warningLength + 0.5): Promise<this> {
    this.x(duration / 4);
    const timeoutID = window.setTimeout(() => {
      this.warn(text, 'red', duration * 0.75);
    }, duration * (1000 / 3));
    this.timeouts.push(timeoutID);
    return utils.wait(duration).then(() => this);
  }

  /**
   * Display a warning text
   * @param text string for warning
   * @param color optional
   * @param duration in seconds
   */
  warn(text: string, color?: string, duration = this.options.warningLength): Promise<this> {
    const fades = duration / 5;
    const delay = duration * (4 / 5);

    this.text(text, color);
    this.go(fades, delay);
    return utils.wait(duration).then(() => this);
  }
}
